'use client';

import { PathEnum } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

export type ScheduleSortOption = 'date' | 'alphabetical';

interface ScheduleSortingProps {
    currentSort: ScheduleSortOption;
}

const ScheduleSorting = ({ currentSort }: ScheduleSortingProps) => {
    const t = useTranslations();
    const router = useRouter();
    const pathname = usePathname();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const sortOptions = [
        { value: 'alphabetical', label: t('Sorting.alphabetical') },
        { value: 'date', label: t('Sorting.byDate') },
    ];

    const currentOption = sortOptions.find((option) => option.value === currentSort);

    const resolveBasePath = () => {
        const pathKey = pathname as keyof typeof PathEnum;
        return PathEnum[pathKey] ?? pathname;
    };

    const handleSortChange = (newSort: ScheduleSortOption) => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort', newSort);
        setIsDropdownOpen(false);

        const basePath = resolveBasePath();
        const query = params.toString();
        router.push(query ? `${basePath}?${query}` : basePath);
    };

    const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div className="flex items-center gap-4">
            <span className="font-helvetica-neue font-light text-gray-200 text-md">
                {t('Sorting.title')}
            </span>
            <div className="relative" ref={dropdownRef}>
                <button
                    className={`flex gap-2.5 max-lg:text-[22px] items-center text-2xl uppercase p-1 border-b hover:text-primary hover:border-primary transition-colors duration-300 ${isDropdownOpen ? 'text-primary border-primary' : 'text-white border-white'}`}
                    onClick={toggleDropdown}
                    type="button"
                >
                    {currentOption?.label}
                    <svg
                        width="13"
                        height="8"
                        viewBox="0 0 13 8"
                        fill="none"
                        stroke="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: `rotate(${isDropdownOpen ? '0' : '180deg'})` }}
                        className="transform transition-transform duration-300"
                    >
                        <path d="M12 6.5L6.5 1.5L1 6.5" strokeWidth="2" />
                    </svg>
                </button>
                {isDropdownOpen && (
                    <div className="lg:absolute lg:top-[calc(100%+16px)] lg:right-0 z-20 bg-black">
                        <div className="lg:min-w-[10rem] max-lg:px-0 lg:w-max p-5 flex flex-col lg:gap-7 gap-3 lg:border">
                            <div className="flex flex-col gap-3.5">
                                {sortOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() =>
                                            handleSortChange(option.value as ScheduleSortOption)
                                        }
                                        className={`text-left text-2xl uppercase hover:text-primary transition-colors duration-300 ${
                                            currentSort === option.value
                                                ? 'text-primary'
                                                : 'text-white'
                                        }`}
                                        type="button"
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScheduleSorting;
